/**
 * DebugPanel - Debug controls for development
 * Includes time scale control, FPS display, and other debug features
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';
import './DebugPanel.css';

interface DebugPanelProps {
  visible?: boolean;
  onToggle?: () => void;
  nightProgress?: number;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({
  visible = false,
  onToggle,
  nightProgress = 0
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed
  const [timeScale, setTimeScale] = useState(1.0);
  const [fps, setFps] = useState(60);
  const [isPaused, setIsPaused] = useState(false);
  const [noclip, setNoclip] = useState(false);
  const position = useSelector((state: RootState) => state.player.position);

  // FPS counter
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();

    const updateFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      const delta = currentTime - lastTime;

      if (delta >= 1000) {
        setFps(Math.round((frameCount * 1000) / delta));
        frameCount = 0;
        lastTime = currentTime;
      }

      requestAnimationFrame(updateFPS);
    };

    const animationId = requestAnimationFrame(updateFPS);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Update global time scale when changed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugTimeScale = timeScale;
    }
  }, [timeScale]);

  const handleTimeScaleChange = (value: number) => {
    setTimeScale(value);
    console.log(`⏱️ Time scale set to ${value}x`);
  };

  const handlePauseToggle = () => {
    const newPauseState = !isPaused;
    setIsPaused(newPauseState);
    if (newPauseState) {
      handleTimeScaleChange(0);
    } else {
      handleTimeScaleChange(1.0);
    }
  };

  const presetSpeeds = [
    { label: '0.5x', value: 0.5 },
    { label: '1x', value: 1.0 },
    { label: '2x', value: 2.0 },
    { label: '5x', value: 5.0 },
    { label: '10x', value: 10.0 },
    { label: '50x', value: 50.0 },
    { label: '100x', value: 100.0 },
    { label: '500x', value: 500.0 },
    { label: '1000x ⚡', value: 1000.0 }, // Ultra fast for quick night completion
  ];

  // ALWAYS show the toggle button and render the component
  return (
    <div className={`debug-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Toggle button - ALWAYS visible in lower right corner */}
      <button
        className="debug-toggle"
        onClick={() => {
          setIsExpanded(!isExpanded);
          console.log(`Debug panel ${!isExpanded ? 'expanded' : 'collapsed'}`);
        }}
        title="Click to toggle Debug Panel (or press ~ key)"
      >
        {isExpanded ? '◀' : '▶'} DEBUG
      </button>

      {/* Expanded content - only show when expanded */}
      {isExpanded && (
        <div className="debug-content">
          {/* Stats Display */}
          <div className="debug-section">
            <div className="debug-label">FPS</div>
            <div className={`debug-value fps ${fps < 30 ? 'fps-low' : fps < 50 ? 'fps-medium' : 'fps-high'}`}>
              {fps}
            </div>
          </div>

          <div className="debug-section">
            <div className="debug-label">Player Position</div>
            <div className="debug-value">X: {Math.round(position.x)}</div>
            <div className="debug-value">Y: {Math.round(position.y)}</div>
          </div>

          <div className="debug-section">
            <div className="debug-label">Night Progress</div>
            <div className="debug-value">{nightProgress.toFixed(1)}%</div>
            <div style={{ width: '100%', height: '10px', background: '#333', marginTop: '5px' }}>
              <div style={{ width: `${nightProgress}%`, height: '100%', background: '#0f0', transition: 'width 0.3s' }} />
            </div>
          </div>

          {/* Time Scale Control */}
          <div className="debug-section">
            <div className="debug-label">Time Scale: {timeScale}x</div>

            {/* Preset buttons */}
            <div className="debug-presets">
              {presetSpeeds.map(preset => (
                <button
                  key={preset.value}
                  className={`preset-button ${timeScale === preset.value ? 'active' : ''}`}
                  onClick={() => handleTimeScaleChange(preset.value)}
                  disabled={isPaused && preset.value !== 0}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Slider */}
            <input
              type="range"
              min="0"
              max="1000"
              step="1"
              value={timeScale}
              onChange={(e) => handleTimeScaleChange(parseFloat(e.target.value))}
              className="debug-slider"
              disabled={isPaused}
            />
          </div>

          {/* Pause/Play Toggle */}
          <div className="debug-section">
            <button
              className={`debug-button pause-button ${isPaused ? 'paused' : 'playing'}`}
              onClick={handlePauseToggle}
            >
              {isPaused ? '▶ Resume' : '⏸ Pause'}
            </button>
          </div>

          {/* Noclip Toggle */}
          <div className="debug-section">
            <button
              className={`debug-button ${noclip ? 'paused' : ''}`}
              onClick={() => {
                const enabled = (window as any).toggleNoclip?.() ?? !noclip;
                setNoclip(enabled);
              }}
              title="Fly through walls and floors. W/S = up/down, A/D = left/right."
            >
              {noclip ? 'Noclip: ON' : 'Noclip: OFF'}
            </button>
          </div>

          {/* Info */}
          <div className="debug-section debug-info">
            <div className="debug-info-text">
              Use time scale to speed up or slow down the night.
            </div>
            <div className="debug-info-text">
              Press <kbd>~</kbd> to toggle this panel.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugPanel;
